import React, { useMemo, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useUserContext } from "../../../utils/hooks/UserContext";
import { coachMenu, userMenu } from "./SidebarListingData";
import { isUserProfileComplete } from "../../../User/utils/profileCompletion";
import { LogoutSVGs } from "./SVGs";
import { userImage, coachImage } from "../../../utils/api";
import { toast } from "react-toastify";
import { FaChevronDown, FaTools, FaUsers, FaLayerGroup, FaSuitcase, FaCogs, FaUserTie, FaHeadset, FaWallet } from "react-icons/fa";

function CoachSideMenuComponent({ collapsed = false, isOpen = false, onClose = () => { }, isMobileView = false }) {
  const { handleLogout, user, getUserFromToken } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [img, setImage] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([
    "Account & Setup", "Programs & Sessions", "Communication", "Content", "Business", "System",
    "My Coaches Feed", "Payments"
  ]);

  const userType = user?.userType;

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const menuItems = useMemo(
    () => (userType === "coach" ? coachMenu : userMenu),
    [userType]
  );

  const isProfileComplete = useMemo(() => {
    if (userType === "coach") return user?.profile_complete === "Yes";
    if (userType === "user") return isUserProfileComplete(user);
    return false;
  }, [userType, user]);

  const onClickLogOut = () => {
    handleLogout();
    setTimeout(() => {
      navigate("/");
    }, 200);
  };

  const onChangeImage = async (file) => {
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error("Image size must be less than 5MB");
        return;
      }
    }
    try {
      setImage(file);
      const formData = new FormData();
      formData.append("profile_pic", file);
      if (userType === "user") await userImage(formData);
      else if (userType === "coach") await coachImage(formData);
      await getUserFromToken();
      toast.success("Profile image updated");
    } catch (e) {
      toast.error("Failed to update profile image");
    }
  };

  const getImage = (image) => {
    if (img) return URL.createObjectURL(img);
    if (image) {
      if (userType === "user") return `${process.env.REACT_APP_USER_PROFILE_FOLDER_PATH}${image}`;
      else if (userType === "coach") return `${process.env.REACT_APP_COACH_PROFILE_FOLDER_PATH}${image}`;
    }
    return "/default.avif";
  };

  // Helper to render a single nav item
  const renderNavItem = (item, isDisabled = false) => {
    const matchPaths = Array.isArray(item.matchPaths) && item.matchPaths.length > 0 ? item.matchPaths : [item.path];
    const isActive = matchPaths.some((p) => location.pathname.startsWith(p));

    // Check if any sub-item is active
    const isSubItemActive = item.subItems && item.subItems.some(sub =>
      location.pathname + location.search === sub.path || location.pathname.startsWith(sub.path.split('?')[0])
    );

    // Determine if this item should be disabled
    const shouldDisable = isMenuItemDisabled(item.label);

    if (shouldDisable) {
      return (
        <div key={item.label}>
          <div
            className="sidebar-nav-item disabled opacity-50 cursor-not-allowed pointer-events-none"
            title="Complete your profile to access this feature"
          >
            {!collapsed && (
              <span className="nav-icon">
                {item.component}
              </span>
            )}
            {!collapsed && <span className="nav-label">{item.label}</span>}
          </div>
        </div>
      );
    }

    return (
      <div key={item.label}>
        <NavLink
          to={item.path}
          onClick={() => isMobileView && isOpen && onClose()}
          className={`sidebar-nav-item ${isActive || isSubItemActive ? 'active' : ''}`}
          title={collapsed ? item.label : undefined}
        >
          {!collapsed && (
            <span className="nav-icon">
              {item.component}
            </span>
          )}
          {!collapsed && <span className="nav-label">{item.label}</span>}
          {(isActive || isSubItemActive) && !collapsed && <span className="ml-auto"><FaChevronDown className="-rotate-90 text-[10px]" /></span>}
        </NavLink>

        {/* Render sub-items if present and parent is active or sub-item is active */}
        {item.subItems && (isActive || isSubItemActive) && !collapsed && (
          <div className="sidebar-sub-items-list">
            {item.subItems.map(sub => {
              const isSubActive = location.pathname + location.search === sub.path;
              return (
                <Link
                  key={sub.label}
                  to={sub.path}
                  className={`sidebar-sub-item ${isSubActive ? 'active' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    {sub.label === "Posts" ? (
                      <img src="/userpost.svg" alt="Posts" className="w-4 h-4" />
                    ) : sub.label === "Free Sessions" || sub.label === "Free Live Sessions" ? (
                      <img src="/caochfreesession.svg" alt="Free Sessions" className="w-4 h-4" />
                    ) : (
                      <img src="/coachshort.svg" alt="Free Sessions" className="w-4 h-4" />
                      // <div className={`w-1.5 h-1.5 rounded-full ${isSubActive ? 'bg-[#00504E]' : 'bg-gray-300'}`}></div>
                    )}
                    {sub.label}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Filter menu items based on profile completion for coaches
  const getAccessibleMenuItems = (items, isComplete) => {
    // For coaches, return all items but mark which ones should be disabled
    if (userType !== "coach") {
      return items;
    }

    // Return all items - we'll handle disabling in renderNavItem
    return items;
  };

  // Check if a menu item should be disabled
  const isMenuItemDisabled = (itemLabel) => {
    if (userType !== "coach" || isProfileComplete) {
      return false;
    }
    // During onboarding, ALL items including "My Profile" should be disabled
    // because the profile page is protected and redirects to onboarding
    return true;
  };

  // Category Icons Mapping
  const getCategoryIcon = (category) => {
    switch (category) {
      case "Account & Setup":
      case "Setup & Operations":
        return <img src="/setupoperation.svg" alt="Setup" className="w-5 h-5" />;
      case "Programs & Sessions":
        return <img src="/userprogramsandsession.svg" alt="Programs & Sessions" className="w-5 h-5" />;
      case "Communication":
      case "Clients & Communication":
        return <img src="/usercommunication.svg" alt="Communication" className="w-5 h-5" />;
      case "Content":
      case "Content & Visibility":
      case "My Coaches Feed":
        return <img src="/usercoachesfeed.svg" alt="Content" className="w-5 h-5" />;
      case "Business":
      case "Payments":
        return <img src="/coachnewbusiness.svg" alt="Business" className="w-5 h-5" />;
      case "System":
        return <img src="/usersystem.svg" alt="System" className="w-5 h-5" />;
      default:
        return <FaTools />;
    }
  };

  return (
    <div className={`${isMobileView
      ? 'w-full h-full overflow-y-auto overflow-x-hidden z-50 flex-shrink-0 transition-all duration-300 hideScrollBar'
      : `${collapsed ? 'w-[60px]' : 'w-[240px]'} sticky top-[100px] h-[calc(100vh-100px)] overflow-y-auto overflow-x-hidden z-40 flex-shrink-0 mb-6 transition-all duration-300 hideScrollBar`
      }`}>
      <div className="rounded-lg h-full flex flex-col shadow-sm" style={{ backgroundColor: '#F8F8F8' }}>
        {!collapsed && (
          <div className="px-2 pt-4 pb-4 mt-1 text-center w-full">
            <h2 className="sidebar-workspace-header">My Coaching Workspace</h2>
          </div>
        )}

        <div className="flex flex-col flex-1 px-3 mt-1 overflow-y-auto hideScrollBar">
          {menuItems.map((categoryObj) => {
            // Check if it's categorized structure
            if (categoryObj.category) {
              const isExpanded = expandedCategories.includes(categoryObj.category);
              return (
                <div key={categoryObj.category} className="sidebar-category-container">
                  {!collapsed && (
                    <div
                      className={`sidebar-category-header ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleCategory(categoryObj.category)}
                    >
                      <div className="category-info">
                        <span className="category-icon text-[#00504E]">
                          {getCategoryIcon(categoryObj.category)}
                        </span>
                        <span className="category-label">{categoryObj.category}</span>
                      </div>
                      <FaChevronDown className="chevron-icon text-[12px]" />
                    </div>
                  )}

                  {(isExpanded || collapsed) && (
                    <div className="sidebar-items-list">
                      {categoryObj.items.map(item => renderNavItem(item))}
                    </div>
                  )}
                </div>
              );
            } else {
              // Fallback for flat items
              return renderNavItem(categoryObj);
            }
          })}

          <div className="logout-container-redesign mt-4">
            <button
              className="logout-btn-redesign"
              onClick={() => {
                if (isMobileView && isOpen) onClose();
                onClickLogOut();
              }}
            >
              <LogoutSVGs />
              {!collapsed && <span>Log out</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoachSideMenuComponent;
